import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { WsMessages } from '@bandido/shared/constants';
import { HotelAvailabilityEmit, HotelAvailabilityScope, HotelFareFinalDto, HotelFareScope } from '@bandido/shared/dtos';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { routes } from '@frontend/router/routes';
import { CONFIG } from '@frontend/config';
import { useWS } from '@frontend/features/common/hooks/useWS';

import { HotelAndHistories, updateHotelAvailabilityRecords } from '../helpers';
import { HotelPropertySearchDTO, LocationSelection, RoomSelection, SearchData, SearchPropertyData } from './types';
import { appendHotelAvailabilityQueryParamsToUrl, appendHotelPropertyQueryParamsToUrl, parseSearchData } from './helpers';
import { HotelWebSocketsEvents } from '@bandido/shared/types';
import { HotelFareScopeStatus, HotelPropertyFareScopeStatus } from '../../HotelPropertyFaresScopeStatus';
import HotelAvailabilityModal from '../../HotelAvailabilityScopeStatus';
import { fromDateToYYYYMMDD } from '@frontend/features/common/helper';

type HotelSearchType = 'location' | 'property';

type HotelSearchContextState = {
  searchData: SearchData;
  searchPropertyData: SearchPropertyData;
  filters: HotelFilterOptions;
  filteredHotels: HotelAndHistories[];
  propertyFares: HotelFareFinalDto[];
  isHotelSearchLoading: boolean;
  setSearchType: (value: HotelSearchType) => void;
  setSearchLocation: (value: LocationSelection) => void;
  setSearchProperty: (value: HotelPropertySearchDTO) => void;
  setSearchRooms: (value: RoomSelection[]) => void;
  setSearchFromDate: (value: Date) => void;
  setSearchToDate: (value: Date) => void;
  updateSearchFilters: (value: HotelFilterOptions) => void;
  setSearchData: (data: SearchData) => void;
  handleSubmitSearch: () => void;
  toggleShowPropertyFaresScope: () => void;
  toggleShowAvailabilityScope: () => void;
  searchId: string | null;
  filtersPropertyFares: HotelFaresFiltersState;
  setFiltersPropertyFares: React.Dispatch<React.SetStateAction<HotelFaresFiltersState>>;
  filtersPropertyFaresOptions: HotelFaresFilterOptionsState
};

const defaultRooms = [{ paxes: [30, 30] }];

export type HotelOrderByOptions = 'cheapest' | 'not-economic' | 'recomended';

export interface HotelFaresFiltersState {
  board?: string;
  accessCode?: string;
  reembolsable?: boolean;
  roomName?: string;
  priceRange?: [number, number];
}

export interface HotelFaresFilterOptionsState {
  priceRange: [number, number];
  boardOptions: string[];
  accessCodeOptions: string[];
}

export interface HotelFilterOptions {
  minPrice?: number;
  maxPrice?: number;
  chainCodes?: string[];
  exclusiveDeals?: boolean[];
  categoryIds?: string[];
  amenities?: string[];
  orderBy?: HotelOrderByOptions;
  options?: {
    chainCodes: string[];
    categoryIds?: string[];
    amenities?: string[];
  };
  priceLimits?: {
    min: number;
    max: number;
  };
  hotelName?: string;
}

const HotelSearchContext = createContext<HotelSearchContextState>({
  filtersPropertyFaresOptions: {
    priceRange: [0, 1000],
    boardOptions: [],
    accessCodeOptions: [],
  },
  searchId: null,
  filters: {
    hotelName: '',
  },
  filteredHotels: [],
  propertyFares: [],
  searchData: {
    location: null,
    from: null,
    to: null,
    rooms: null,
  },
  searchPropertyData: {
    property: null,
    from: null,
    to: null,
    rooms: null,
  },
  isHotelSearchLoading: false,
  setSearchData: () => { },
  setSearchType: () => { },
  setSearchProperty: () => { },
  setSearchLocation: () => { },
  setSearchRooms: () => { },
  setSearchFromDate: () => { },
  setSearchToDate: () => { },
  updateSearchFilters: () => { },
  handleSubmitSearch: () => { },
  toggleShowPropertyFaresScope: () => { },
  toggleShowAvailabilityScope: () => { },
  filtersPropertyFares: {},
  setFiltersPropertyFares: () => { },
});

export const HotelSearchContextProvider = ({ children }: PropsWithChildren) => {
  const { isConnected, socketRef, sendMessage, leaveRoom, joinRoom } = useWS(`${CONFIG.WS_URL}/hotel-availability`);

  const [searchId, setSearchId] = useState<string | null>(null);
  const [showAvailabilityScope, setShowAvailabilityScope] = useState<boolean>(false);
  const [hotelAvailabilityList, setHotelAvailabilityList] = useState<HotelAndHistories[]>([]);
  const [availabilityFaresScope, setAvailabilityFaresScope] = useState<HotelAvailabilityScope | null>(null);
  const [showPropertyFaresScope, setShowPropertyFaresScope] = useState<boolean>(false);
  const [propertyFaresScope, setPropertyFaresScope] = useState<HotelFareScopeStatus | null>(null);
  const [propertyFares, setPropertyFares] = useState<HotelFareFinalDto[]>([]);
  const [filters, setFilters] = useState<HotelFilterOptions>({ orderBy: 'cheapest', hotelName: '' });
  const [filtersPropertyFaresOptions, setFiltersPropertyFaresOptions] = useState<HotelFaresFilterOptionsState>({
    priceRange: [0, 1000],
    boardOptions: [],
    accessCodeOptions: [],
  });
  const [filtersPropertyFares, setFiltersPropertyFares] = useState<HotelFaresFiltersState>({
    board: '',
    accessCode: '',
    reembolsable: false,
    roomName: '',
    priceRange: [0, 1000],
  });
  const [isJoinedToRoom, setIsJoinedToRoom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<LocationSelection | null>(null);
  const [property, setProperty] = useState<HotelPropertySearchDTO | null>(null);
  const [searchType, setType] = useState<HotelSearchType>('location');
  const [fromDate, setFromDate] = useState<Date | null>();
  const [toDate, setToDate] = useState<Date | null>();
  const [rooms, setRooms] = useState<RoomSelection[] | null>([
    { paxes: [30, 30] }
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const searchData = useMemo(
    () => ({
      location: location || null,
      from: fromDate || null,
      to: toDate || null,
      rooms: rooms || null,
    }),
    [location, fromDate, toDate, rooms],
  );

  const searchPropertyData = useMemo(
    () => ({
      property: property || null,
      from: fromDate || null,
      to: toDate || null,
      rooms: rooms || null,
    }),
    [property, fromDate, toDate, rooms],
  );

  const filteredHotels = useMemo(() => {
    if (hotelAvailabilityList.length === 0) return [];

    const {
      hotelName = '',
      minPrice = 0,
      maxPrice = Number.MAX_VALUE,
      chainCodes = [],
      categoryIds = [],
      amenities = [],
      orderBy = 'cheapest'
    } = filters;

    let hotels = hotelAvailabilityList.filter((hotel) => {
      if (hotelName && !hotel.name?.toLowerCase().includes(hotelName.toLowerCase())) {
        return false;
      }
      
      if (hotel.minPrice < minPrice || hotel.minPrice > maxPrice) {
        return false;
      }
      
      if (chainCodes.length > 0 && !chainCodes.includes(hotel.chainCode!)) {
        return false;
      }
      
      if (categoryIds.length > 0 && !categoryIds.includes(String(hotel.category))) {
        return false;
      }
      
      if (amenities.length > 0 && 
          !amenities.some((amenity) => hotel.amenities?.some((a: any) => a.code === amenity))) {
        return false;
      }

      return true;
    });

    if (orderBy === 'cheapest') {
      hotels.sort((a, b) => a.minPrice - b.minPrice);
    } else if (orderBy === 'not-economic') {
      hotels.sort((a, b) => b.minPrice - a.minPrice);
    }

    return hotels;
  }, [hotelAvailabilityList, filters]);

  const handleRoomTransition = useCallback((newSearchId: string) => {
    if (searchId && isJoinedToRoom) {
      leaveRoom(JSON.stringify({ searchId }));
      setIsJoinedToRoom(false);
    }
    setSearchId(newSearchId);
  }, [searchId, isJoinedToRoom, leaveRoom]);

  const handleWebSocketError = useCallback((error?: string) => {
    setIsLoading(false);
    addToast({
      color: 'error',
      message: error || t('hotels.availability.error'),
    });
  }, [addToast, t]);

  const updatePropertyFaresScopeReceived = useCallback((accessCode: string) => {
    setPropertyFaresScope((prevScope) => {
      if (!prevScope) return prevScope;
      return {
        ...prevScope,
        suppliers: prevScope.suppliers.map((supplier) =>
          supplier.accessCode === accessCode
            ? { ...supplier, recievied: true }
            : supplier),
      };
    });
  }, []);

  const updateAvailabilityScopeReceived = useCallback((accessCode: string, hotelCodes: string[]) => {
    setAvailabilityFaresScope((prevScope) => {
      if (!prevScope) return prevScope;
      return {
        ...prevScope,
        suppliers: prevScope.suppliers.map((supplier) => {
          if (supplier.accessCode !== accessCode) return supplier;
          return {
            ...supplier,
            hotels: supplier.hotels.map((hotel) =>
              hotelCodes.includes(hotel.hotelCode)
                ? { ...hotel, received: true }
                : hotel),
          };
        }),
      };
    });
  }, []);

  const handleRequestAvailabilityWS = useCallback(async (withSearchId?: string) => {
    const { location, from, to, rooms } = searchData;
    if (!searchData.location || !searchData.from || !searchData.to || !searchData.rooms || !isConnected) {
      return;
    }

    const currentSearchId = withSearchId || uuid();
    
    if (!withSearchId) {
      handleRoomTransition(currentSearchId);
    }

    resetFiltersToDefaults();
    resetSearchData();

    try {
      sendMessage(
        WsMessages.getHotelAvailability,
        JSON.stringify({
          request: {
            agencyId: CONFIG.AGENCY_ID,
            latitude: location?.latitude,
            longitude: location?.longitude,
            checkIn: fromDateToYYYYMMDD(from || ''),
            checkOut: fromDateToYYYYMMDD(to || ''),
            placeId: location?.placeId,
            occupancies: rooms?.map(({ paxes }) => ({ paxes: paxes.map((age) => ({ age })) })),
          },
          searchId: currentSearchId,
        }),
      );
    } catch (_e) {
      setIsLoading(false);
      addToast({
        color: 'error',
        message: t('hotels.availability.error'),
      });
    }
  }, [searchData, isConnected, handleRoomTransition]);

  const handleRequestPropertyFaresWS = useCallback(async (withSearchId?: string) => {
    const { from, to, rooms } = searchData;
    if (!searchData.from || !searchData.to || !searchData.rooms || !isConnected) {
      return;
    }

    const currentSearchId = withSearchId || uuid();
    
    if (!withSearchId) {
      handleRoomTransition(currentSearchId);
    } else {
      setSearchId(currentSearchId);
    }

    resetFiltersToDefaults();
    resetSearchData();

    try {
      sendMessage(
        WsMessages.getHotelPropertyFares,
        JSON.stringify({
          request: {
            agencyId: CONFIG.AGENCY_ID,
            propertyId: property?.nimbuxHotelId || searchParams.get('propertyId'),
            checkIn: fromDateToYYYYMMDD(from || ''),
            checkOut: fromDateToYYYYMMDD(to || ''),
            placeId: location?.placeId,
            occupancies: rooms?.map(({ paxes }) => ({ paxes: paxes.map((age) => ({ age })) })),
          },
          searchId: currentSearchId
        }),
      );
    } catch (_e) {
      setIsLoading(false);
      addToast({
        color: 'error',
        message: t('hotels.availability.error'),
      });
    }
  }, [searchData, isConnected, property, location, handleRoomTransition]);

  const setSearchData = useCallback((data: SearchData) => {
    setLocation(data.location);
    setFromDate(data.from);
    setToDate(data.to);
    setRooms(data.rooms);
  }, []);

  const updateSearchFilters = useCallback(
    (value: HotelFilterOptions) => setFilters((prev) => ({ ...prev, ...value })),
    [],
  );

  const updateFiltersFromHotels = useCallback(() => {
    if (hotelAvailabilityList.length === 0) {
      updateSearchFilters({
        priceLimits: { min: 0, max: Number.MAX_VALUE },
        options: { chainCodes: [], categoryIds: [], amenities: [] },
        minPrice: 0,
        maxPrice: Number.MAX_VALUE,
        chainCodes: filters.chainCodes || [],
        exclusiveDeals: filters.exclusiveDeals || [],
        categoryIds: filters.categoryIds || [],
        amenities: filters.amenities || [],
        hotelName: filters.hotelName || '',
      });
      return;
    }

    const prices = hotelAvailabilityList.map((hotel) => hotel.minPrice);
    const chainCodes = Array.from(
      new Set(hotelAvailabilityList.map((hotel) => hotel.chainCode).filter(Boolean)),
    ) as string[];
    const categoryIds = Array.from(
      new Set(hotelAvailabilityList.map((hotel) => hotel.category).filter(Boolean)),
    ) as number[];
    const amenities = Array.from(
      new Set(hotelAvailabilityList.flatMap((hotel) => 
        hotel.amenities?.map((a: any) => a.code) || [])),
    );

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    updateSearchFilters({
      priceLimits: { min: minPrice, max: maxPrice },
      options: { chainCodes, categoryIds: categoryIds.map(String), amenities },
      minPrice,
      maxPrice,
      chainCodes: filters.chainCodes || [],
      exclusiveDeals: filters.exclusiveDeals || [],
      categoryIds: filters.categoryIds || [],
      amenities: filters.amenities || [],
      hotelName: filters.hotelName || '',
    });
     }, [hotelAvailabilityList, filters.chainCodes, filters.exclusiveDeals, filters.categoryIds, filters.amenities, filters.hotelName, updateSearchFilters]);

  useEffect(() => {
    const { location, rooms, from, to } = parseSearchData(searchParams);

    setLocation(location);
    setRooms(rooms || [...defaultRooms]);
    setFromDate(from);
    setToDate(to);
  }, [searchParams]);

  useEffect(() => {
    if (!socketRef.current || !isConnected) return;

    const handleFaresScopeSuccess = (msg: string) => {
      try {
        const parsed = JSON.parse(msg) as HotelFareScope;
        const { suppliers, hash, searchId: msgSearchId } = parsed;
        if (searchId === msgSearchId) {
          setPropertyFaresScope({
            hash: hash || '',
            suppliers: suppliers.map(s => ({
              ...s,
              recievied: false
            }))
          });
        }
      } catch (e) {
        console.error('Error handling fares scope success:', e);
      }
    };

    const createEquivalentFareKey = (fare: HotelFareFinalDto) => {
      // Campos que definen una tarifa sea la misma
      const keyFields = {
        boardDescription: fare.boardCode,
        paymentType: fare.paymentType,
        currency: fare.currency,
        refundable: fare.refundable,
        remarks: fare.remarks,
        rateRules: JSON.stringify(fare.rateRules || []),
        cancelPolicy: JSON.stringify(fare.cancelPolicy || {}),
        cancelPenalties: JSON.stringify(fare.cancelPenalties || {}),
        rooms: JSON.stringify(fare.rooms || []),
        occupancies: JSON.stringify(fare.occupancies || []),
        surcharges:  JSON.stringify(fare.surcharges || []),
      };
      
      return JSON.stringify(keyFields);
    };

    const handleFaresSuccess = (msg: string) => {
      try {
        const parsed = JSON.parse(msg) as { propertyFares: HotelFareFinalDto[]; searchId: string };
        const { propertyFares, searchId: msgSearchId } = parsed;

        if (Array.isArray(propertyFares) && searchId === msgSearchId) {
          setPropertyFares(prev => {
            // FASE 1: Deduplicación por optionRefId
            const existingRefIds = new Set(prev.map(fare => fare.optionRefId));
            const newFares = propertyFares.filter(fare => !existingRefIds.has(fare.optionRefId));
            const allFares = [...prev, ...newFares];
            
            // FASE 2: Agrupar por "tarifa equivalente" y quedarse con la más barata
            const equivalentFaresMap = new Map<string, HotelFareFinalDto>();
            
            allFares.forEach(fare => {
              const equivalentKey = createEquivalentFareKey(fare);
              const existing = equivalentFaresMap.get(equivalentKey);
              
              if (!existing || fare.finalPrice < existing.finalPrice) {
                equivalentFaresMap.set(equivalentKey, fare);
              }
            });
            
            const updated = Array.from(equivalentFaresMap.values());
            
            const allPrices = updated.map((fare) => fare.finalPrice);
            
            const minPrice = allPrices.length ? Math.min(...allPrices) : 0;
            const maxPrice = allPrices.length ? Math.max(...allPrices) : 1000;
            const allAccessCodes = Array.from(new Set(updated.map((fare) => fare.context)));
            const allBoardDescriptions = Array.from(new Set(updated.map((fare) => fare.boardDescription).filter(Boolean)));
        
            setFiltersPropertyFaresOptions({
              priceRange: [minPrice, maxPrice],
              accessCodeOptions: allAccessCodes,
              boardOptions: allBoardDescriptions,
            });

            return updated;
          });

          if (propertyFares[0]?.accessCode) {
            updatePropertyFaresScopeReceived(propertyFares[0].accessCode);
          }
        }
      } catch (e) {
        console.error('Error handling fares success:', e);
      }
    };

    const handleAvailabilityScopeSuccess = (msg: string) => {
      try {
        const parsed = JSON.parse(msg) as HotelAvailabilityScope;
        const { suppliers, hash, searchId: msgSearchId } = parsed;
        if (searchId === msgSearchId) {
          setAvailabilityFaresScope({
            searchId,
            hash: hash || '',
            suppliers: suppliers.map(s => ({
              ...s,
              recievied: false,
            }))
          });
        }
      } catch (e) {
        console.error('Error handling availability scope success:', e);
      }
    };

    const handleAvailabilitySuccess = (msg: string) => {
      try {
        const JsonMsg = JSON.parse(msg) as { hash: string; items: HotelAvailabilityEmit[]; searchId: string; };
        const { items, searchId: msgSearchId } = JsonMsg;

        if (searchId === msgSearchId) {
          setHotelAvailabilityList((prev) => updateHotelAvailabilityRecords(prev, items));
          if (items.length) {
            setIsLoading(false);
            const [item] = items;
            const { accessCode } = item;
            const hotelCodes = items.map(i => i.hotelCode);
            updateAvailabilityScopeReceived(accessCode, hotelCodes);
          }
        }
      } catch (e) {
        console.error('Error handling availability success:', e);
      }
    }

    const handleJoinedRoom = () => setIsJoinedToRoom(true);

    const eventHandlers = [
      [HotelWebSocketsEvents.FARES_SCOPE_SUCCESS, handleFaresScopeSuccess],
      [HotelWebSocketsEvents.FARES_SUCCESS, handleFaresSuccess],
      [HotelWebSocketsEvents.AVAILABILITY_SCOPE_SUCCESS, handleAvailabilityScopeSuccess],
      [HotelWebSocketsEvents.AVAILABILITY_SUCCESS, handleAvailabilitySuccess],
      [HotelWebSocketsEvents.AVAILABILITY_ERROR, handleWebSocketError],
      [HotelWebSocketsEvents.JOINED_ROOM, handleJoinedRoom],
    ] as const;

    eventHandlers.forEach(([event, handler]) => {
      socketRef.current!.on(event, handler);
    });

    return () => {
      if (socketRef.current) {
        eventHandlers.forEach(([event, handler]) => {
          socketRef.current!.off(event, handler);
        });
      }
    };
  }, [isConnected, searchId, handleWebSocketError, updatePropertyFaresScopeReceived, updateAvailabilityScopeReceived]);

  useEffect(() => {
    if (!searchId) {
      setSearchId(uuid());
      return;
    }
    
    if (!socketRef.current || !isConnected || isJoinedToRoom) return;
    joinRoom(JSON.stringify({ searchId }));
  }, [isConnected, searchId, isJoinedToRoom, joinRoom]);

  useEffect(() => {
    if (!searchId || !isJoinedToRoom) return;

    const doSearch = searchParams.get('doSearch') === 'true';
    if (doSearch) {
      handleRequestPropertyFaresWS(searchId);
    } else {
      handleRequestAvailabilityWS(searchId);
    }
  }, [isJoinedToRoom, searchParams, searchId, handleRequestPropertyFaresWS, handleRequestAvailabilityWS]);

  useEffect(() => {
    return () => {
      if (socketRef.current && isConnected && searchId && isJoinedToRoom) {
        setIsJoinedToRoom(false);
        leaveRoom(JSON.stringify({ searchId }));
      }
    };
  }, []);

  useEffect(() => {
    updateFiltersFromHotels();
  }, [updateFiltersFromHotels]);

  const toggleShowAvailabilityScope = () => setShowAvailabilityScope(prev => !prev);
  const toggleShowPropertyFaresScope = () => setShowPropertyFaresScope(prev => !prev);

  const resetSearchData = () => {
    setPropertyFares([]);
    setPropertyFaresScope(null);
    setHotelAvailabilityList([]);
    setAvailabilityFaresScope(null);
    setIsLoading(true);
  };

  const resetFiltersToDefaults = () => {
    updateSearchFilters({
      priceLimits: {
        min: 0,
        max: Number.MAX_VALUE,
      },
      options: {
        chainCodes: [],
        categoryIds: [],
        amenities: [],
      },
      minPrice: 0,
      maxPrice: Number.MAX_VALUE,
      chainCodes: [],
      exclusiveDeals: [],
      categoryIds: [],
      amenities: [],
      hotelName: '',
    });
  };

  const handleSubmitSearch = () => {
    const params = searchType === 'location' ? appendHotelAvailabilityQueryParamsToUrl(searchData) : appendHotelPropertyQueryParamsToUrl(searchPropertyData);
    if (params && socketRef.current) {
      setSearchParams(params);

      if (searchType === 'location') {
        setIsLoading(true);
        handleRequestAvailabilityWS();
        navigate(`${routes.hotels.availability}?${params.toString()}`);
      } else if (searchType === 'property') {
        handleRequestPropertyFaresWS();
        navigate(`${routes.hotels.fares}?${params.toString()}`);
      }
    }
  };

  const handleFromDateChange = (newFromDate: Date) => {
    setFromDate(newFromDate);
    if (toDate && newFromDate >= toDate) {
      const newToDate = new Date(newFromDate);
      newToDate.setDate(newToDate.getDate() + 1);
      setToDate(newToDate);
    }
  };

  const handleToDateChange = (newToDate: Date) => {
    setToDate(newToDate);
    if (fromDate && newToDate <= fromDate) {
      const newFromDate = new Date(newToDate);
      newFromDate.setDate(newFromDate.getDate() - 1);
      setFromDate(newFromDate);
    }
  };

  return (
    <HotelSearchContext.Provider
      value={{
        searchData,
        searchPropertyData,
        propertyFares,
        filters,
        filteredHotels,
        toggleShowPropertyFaresScope,
        toggleShowAvailabilityScope,
        setSearchLocation: (value) => setLocation(value),
        setSearchProperty: (value) => setProperty(value),
        setSearchType: (value) => setType(value),
        setSearchRooms: (value) => setRooms(value),
        setSearchFromDate: handleFromDateChange,
        setSearchToDate: handleToDateChange,
        updateSearchFilters,
        setSearchData,
        handleSubmitSearch,
        searchId,
        isHotelSearchLoading: isLoading,
        filtersPropertyFares,
        setFiltersPropertyFares,
        filtersPropertyFaresOptions,
      }}
    >
      {children}
      {
        propertyFaresScope &&
        <HotelPropertyFareScopeStatus
          open={showPropertyFaresScope}
          scope={propertyFaresScope}
          onClose={toggleShowPropertyFaresScope}
        />}
      {
        availabilityFaresScope &&
        <HotelAvailabilityModal
          scope={availabilityFaresScope}
          open={showAvailabilityScope}
          onClose={toggleShowAvailabilityScope}
        />
      }
    </HotelSearchContext.Provider>
  );
};

export const useHotelSearch = () => {
  const ctx = useContext(HotelSearchContext);

  if (!ctx) throw new Error('useHotelSearch must be within HotelSearchContextProvider');

  return ctx;
};
